import { QUOTE_INT_MAX } from '@repo/api-contracts';
import { formatCount } from '@/shared/lib';
import { isDesiredDateAllowed } from './desired-date';
import { estimateQuote, type QuotePriceTier } from './estimate';

/**
 * 견적 요청 폼 공용 규칙 — 회원(`features/quote-request`)과 비회원(`features/guest-quote-request`) 요청이 같은 칸·같은 문구·같은
 * 검증을 쓰도록 한곳에 둔다(같은 계층 feature 끼리는 import 할 수 없다). 서버 규칙(quote-policy)과 같은 조건을 화면에서 먼저 안내한다.
 */

/** 회원·비회원 요청이 함께 받는 칸. */
export const QUOTE_REQUEST_SHARED_FIELD_KEYS = [
  'quantity',
  'printMethodId',
  'desiredDate',
  'memo',
  'companyName',
  'contactName',
] as const;

export type QuoteRequestSharedFieldKey = (typeof QUOTE_REQUEST_SHARED_FIELD_KEYS)[number];

export const isQuoteRequestSharedField = (key: PropertyKey): key is QuoteRequestSharedFieldKey =>
  typeof key === 'string' && (QUOTE_REQUEST_SHARED_FIELD_KEYS as readonly string[]).includes(key);

/** 글자 수 상한 — 계약 스키마(quotes.ts)의 max 와 같다. 입력창 maxLength·안내 문구가 쓴다. */
export const QUOTE_REQUEST_TEXT_LIMITS = {
  memo: 1_000,
  companyName: 100,
  contactName: 50,
} as const;

/** 계약 스키마에는 한글 문구가 없어 칸별 문구를 여기서 붙인다. */
export const QUOTE_REQUEST_INVALID_MESSAGES: Readonly<Record<QuoteRequestSharedFieldKey, string>> =
  {
    quantity: '수량은 1 이상의 정수로 입력해 주세요.',
    printMethodId: '인쇄 방식을 선택해 주세요.',
    desiredDate: '희망 납기일은 YYYY-MM-DD 형식으로 입력해 주세요.',
    memo: '요청 메모를 확인해 주세요.',
    companyName: '회사·단체명을 확인해 주세요.',
    contactName: '담당자명을 입력해 주세요.',
  };

export const QUOTE_REQUEST_TOO_LONG_MESSAGES: Readonly<
  Partial<Record<QuoteRequestSharedFieldKey, string>>
> = {
  memo: '요청 메모는 1,000자 이하로 입력해 주세요.',
  companyName: '회사·단체명은 100자 이하로 입력해 주세요.',
  contactName: '담당자명은 50자 이하로 입력해 주세요.',
};

/** 칸으로 보낼 수 없는 입력 오류(스키마의 알 수 없는 키 등)의 일반 폼 오류. */
export const QUOTE_REQUEST_INVALID_INPUT_MESSAGE = '입력값이 올바르지 않습니다.';

export const SIZE_TOTAL_EMPTY_MESSAGE = '사이즈별 수량을 한 개 이상 입력해 주세요.';
export const SIZE_TOTAL_INVALID_MESSAGE = '사이즈별 수량은 0 이상의 정수로 입력해 주세요.';

const DESIRED_DATE_TOO_EARLY_MESSAGE = '희망 납기일은 내일 이후 날짜로 입력해 주세요.';
/** 서버는 수량 × 구간 단가(+ 배송비)가 int4 를 넘으면 INVALID_QUANTITY 로 거절한다 — 같은 조건을 수량 칸에서 먼저 안내한다. */
const AMOUNT_TOO_LARGE_MESSAGE = '수량이 너무 많습니다. 상담으로 문의해 주세요.';

/** 스키마 오류(zod issue code)에 맞는 칸 문구 — 너무 길면 글자 수 문구, 그 밖에는 형식 문구. */
export const quoteRequestFieldMessage = (
  key: QuoteRequestSharedFieldKey,
  issueCode: string,
): string =>
  issueCode === 'too_big'
    ? (QUOTE_REQUEST_TOO_LONG_MESSAGES[key] ?? QUOTE_REQUEST_INVALID_MESSAGES[key])
    : QUOTE_REQUEST_INVALID_MESSAGES[key];

const INTEGER_PATTERN = /^\d+$/;

/** 입력창 수량 문자열 → 정수. 정수가 아니면 NaN(계약 스키마가 거부한다). */
export const parseQuoteQuantity = (raw: string): number =>
  INTEGER_PATTERN.test(raw.trim()) ? Number(raw.trim()) : Number.NaN;

/** 선택 입력 칸 — 앞뒤 공백을 지우고 비면 null. */
export const blankToNull = (raw: string): string | null => (raw.trim() === '' ? null : raw.trim());

export interface QuoteRequestRuleValues {
  /** 견적 수량(회원·비회원 모두 배송지 수량 합계) 문자열. */
  readonly quantity: string;
  readonly desiredDate: string;
  /** 임시 견적과 같은 배송비 합계 — 상품 금액에 더해 견적 금액 상한(int4)을 본다(서버 초안 금액 규칙과 같다). 없으면 0. */
  readonly shippingAmount?: number;
}

export interface QuoteRequestRuleContext {
  readonly minOrderQty: number;
  /** 임시 견적과 같은 구간 단가 — 수량 × 단가가 견적 금액 상한을 넘는지 본다. */
  readonly priceTiers: readonly QuotePriceTier[];
  /** 희망 납기일 "한국 내일" 판정 기준 시각. */
  readonly now: Date;
}

export type QuoteRequestRuleErrors = Partial<Record<'quantity' | 'desiredDate', string>>;

/**
 * 스키마 밖 규칙(순수 함수) — 최소 주문 수량(상품마다 다르다)·수량 × 단가의 견적 금액 상한·희망 납기일(적었다면 한국 날짜 기준 내일 이후).
 * 서버도 같은 규칙으로 다시 거른다. 스키마 오류가 이미 있는 칸에는 호출한 쪽이 그 오류를 먼저 쓴다(`??=`).
 */
export const checkQuoteRequestRules = (
  {
    quantity: rawQuantity,
    desiredDate: rawDesiredDate,
    shippingAmount = 0,
  }: QuoteRequestRuleValues,
  { minOrderQty, priceTiers, now }: QuoteRequestRuleContext,
): QuoteRequestRuleErrors => {
  const errors: { quantity?: string; desiredDate?: string } = {};

  const quantity = parseQuoteQuantity(rawQuantity);
  if (Number.isInteger(quantity) && quantity < minOrderQty) {
    errors.quantity = `최소 주문 수량은 ${formatCount(minOrderQty)}개입니다.`;
  } else {
    const estimate = estimateQuote(priceTiers, quantity);
    if (estimate !== null && estimate.totalAmount + shippingAmount > QUOTE_INT_MAX) {
      errors.quantity = AMOUNT_TOO_LARGE_MESSAGE;
    }
  }

  const desiredDate = blankToNull(rawDesiredDate);
  if (desiredDate !== null && !isDesiredDateAllowed(desiredDate, now)) {
    errors.desiredDate = DESIRED_DATE_TOO_EARLY_MESSAGE;
  }

  return errors;
};
